"use client";

import dynamic from "next/dynamic";
import OrderBook from "./OrderBook";

const KalshiOrderBook = dynamic(() => import("./KalshiOrderBook"), { ssr: false });
const HyperliquidOrderBook = dynamic(() => import("./HyperliquidOrderBook"), { ssr: false });

interface SourceAwareOrderBookProps {
  source: "polymarket" | "kalshi" | "predict" | "hyperliquid";
  tokenIds?: string[];
  outcomes?: string[];
  acceptingOrders?: boolean;
  initialOutcomeIndex?: number;
  orderbookApi?: "default" | "sports-live";
  marketTicker?: string;
  predictMarketId?: string;
  /** Hyperliquid YES-side slug — orderbook fetched against /api/markets/hyperliquid/[slug]/orderbook */
  hyperliquidSlug?: string;
  /** Hyperliquid NO-side slug — used by the YES↔NO toggle inside the book */
  hyperliquidAltSlug?: string;
  externalData?: { yes: any; no: any } | null;
  hideHeader?: boolean;
  /** Polymarket condition ID — surfaces the liquidity-rewards band
   * shading inside the book. No-op for Kalshi / Predict / Hyperliquid. */
  conditionId?: string | null;
}

export default function SourceAwareOrderBook({
  source,
  tokenIds,
  outcomes,
  acceptingOrders,
  initialOutcomeIndex,
  orderbookApi,
  marketTicker,
  predictMarketId,
  hyperliquidSlug,
  hyperliquidAltSlug,
  externalData,
  hideHeader,
  conditionId,
}: SourceAwareOrderBookProps) {
  if (source === "kalshi") {
    if (!marketTicker) return null;
    return <KalshiOrderBook marketTicker={marketTicker} acceptingOrders={acceptingOrders} hideHeader={hideHeader} />;
  }

  if (source === "hyperliquid") {
    if (!hyperliquidSlug) return null;
    return (
      <HyperliquidOrderBook
        marketSlug={hyperliquidSlug}
        altSlug={hyperliquidAltSlug}
        acceptingOrders={acceptingOrders}
        hideHeader={hideHeader}
      />
    );
  }

  // Polymarket needs tokenIds, Predict needs predictMarketId
  if (source === "predict" && !predictMarketId) return null;
  if (source === "polymarket" && !tokenIds?.length) return null;

  return (
    <OrderBook
      tokenIds={tokenIds || []}
      outcomes={outcomes || []}
      acceptingOrders={acceptingOrders}
      initialOutcomeIndex={initialOutcomeIndex}
      orderbookApi={orderbookApi}
      source={source}
      predictMarketId={predictMarketId}
      externalData={externalData}
      hideHeader={hideHeader}
      alwaysExpanded={hideHeader}
      conditionId={source === "polymarket" ? conditionId : undefined}
    />
  );
}
